import { useIsMounted } from "@/hooks/utils/use-is-mounted";
import { useThrottle } from "@/hooks/utils/use-throttle";
import { cn } from "@/utils/cn";
import { isEqual } from "@/utils/is-equal";
import { useEffect, useRef, useState } from "react";

type WheelPickerProps<T> = {
  options: T[];
  value?: T;
  labelBuilder: (value: T) => string;
  onChange: (value: T) => void;
  itemHeight?: number;
  visibleItemCount?: number; // default: 3
  className?: string;
};

const WheelPicker = <T,>({
  options,
  value,
  labelBuilder,
  onChange,
  itemHeight = 48,
  visibleItemCount = 3,
  className,
}: WheelPickerProps<T>) => {
  const isMounted = useIsMounted();
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleSnapToItemThrottled = useThrottle(handleSnapToItem, 500);
  const isScrollingRef = useRef(false);
  const [active, setActive] = useState<T | undefined>(value);

  const isTouchScrollingRef = useRef(false);

  const offset = Math.floor(visibleItemCount / 2);

  const totalPadding = offset * itemHeight;

  useEffect(() => {
    if (value) {
      const index = options.findIndex((opt) => isEqual(opt, value));
      if (index >= 0) {
        setTimeout(() => {
          setActive(value);
          scrollToIndex(index, "auto");
        }, 100);
      }
    }
  }, [value]);

  function handleSnapToItem() {
    if (!scrollRef.current) return;
    const scrollTop = scrollRef.current.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    const selected = options[index];
    if (selected === undefined) return;
    if (selected !== active) {
      setActive(selected);
      onChange(selected);
    }
  }

  const scrollToIndex = (
    index: number,
    behavior: ScrollBehavior = "smooth",
  ) => {
    if (isScrollingRef.current) return;
    if (!scrollRef.current) return;
    const target = index * itemHeight;
    scrollRef.current.scrollTo({ top: target, behavior });
    isScrollingRef.current = false;
  };

  const onWheel = () => {
    if (!isMounted) return;
    handleSnapToItemThrottled();
  };

  const onTouchStart = () => {
    isTouchScrollingRef.current = true;
  };
  const onTouchMove = () => {
    if (!isMounted) return;
    handleSnapToItemThrottled();
  };
  const onTouchEnd = () => {
    isTouchScrollingRef.current = false;
  };

  const handleClick = (index: number) => {
    if (isTouchScrollingRef.current) return;

    scrollToIndex(index);
    setActive(options[index]);
    onChange(options[index]);
  };

  return (
    <div
      className={cn("relative w-[200px] overflow-hidden", className)}
      style={{ height: `${itemHeight * visibleItemCount}px` }}
    >
      <div
        ref={scrollRef}
        className="h-full overflow-y-scroll scroll-smooth no-scrollbar"
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          paddingTop: `${totalPadding}px`,
          paddingBottom: `${totalPadding}px`,
        }}
      >
        {options.map((opt, index) => {
          const isActive = isEqual(opt, active);
          return (
            <div
              key={index}
              onClick={() => handleClick(index)}
              className={cn(
                "flex items-center justify-center transition-all cursor-pointer",
                isActive
                  ? "text-black font-bold text-xl bg-secondary-light rounded-xl"
                  : "text-base text-gray-500",
              )}
              style={{
                height: `${itemHeight}px`,
                scrollSnapAlign: "center",
              }}
            >
              {labelBuilder(opt)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WheelPicker;
